// =================================================================================
// File: src/pages/visitor/DiscoverWorkshopsPage.jsx
// Description: Browse all public workshops from studios
// =================================================================================

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  ArrowLeft,
  Search,
} from "lucide-react";
import {
  formatPrice,
  formatDateRange,
  getDuration,
} from "../../utils/formatters";
import { usePublicStudio } from "../../contexts/PublicStudioContext";
import WorkshopBookingModalVisitor from "../../components/Modals/WorkshopBookingModalVisitor";
import VisitorNavBar from "../../components/Common/VisitorNavBar";
import { apiFetch } from "../../lib/apiFetch";

/**
 * DiscoverWorkshopsPage - Browse and discover ceramic workshops
 *
 * Behaviour:
 * - Fetches public workshops from public API (server-side cached)
 * - Supports search by title
 * - Shows past workshops when toggle enabled
 * - NO local cache fallback — visitors see only server-provided data
 * - Empty state when no workshops available
 */
export default function DiscoverWorkshopsPage() {
  const navigate = useNavigate();
  const { studioInfo } = usePublicStudio();

  const [workshops, setWorkshops] = useState([]);
  const [filteredWorkshops, setFilteredWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // Public API loader; no local cache
  const [fullscreenWorkshop, setFullscreenWorkshop] = useState(null);
  const [bookingWorkshop, setBookingWorkshop] = useState(null);

  const fetchInFlightRef = useRef(false);
  const lastFetchAtRef = useRef(0);

  async function fetchWorkshopsFromApi({ allowThrottling = true } = {}) {
    if (fetchInFlightRef.current) return;

    const now = Date.now();
    if (allowThrottling && now - lastFetchAtRef.current < 30_000) {
      return;
    }

    fetchInFlightRef.current = true;
    try {
      const res = await apiFetch("/api/public/workshops");
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || "Failed to load workshops");
      }
      const rows = Array.isArray(json) ? json : json?.data;
      const arr = Array.isArray(rows) ? rows : [];
      setWorkshops(arr);
      setFilteredWorkshops(arr);
      setError(null);
      lastFetchAtRef.current = Date.now();
    } catch (e) {
      setError(e?.message || "Failed to load workshops");
      setWorkshops([]);
      setFilteredWorkshops([]);
    } finally {
      fetchInFlightRef.current = false;
      setLoading(false);
    }
  }

  // Initial load (public API)
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);

      await fetchWorkshopsFromApi({ allowThrottling: false });

      if (cancelled) return;
    }

    load();
    return () => { cancelled = true; };
  }, []);

  // Intentionally no Supabase realtime subscription for visitors.
  // Keeps idle tabs idle and avoids opening large numbers of realtime connections at scale.

  // Filter workshops by search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredWorkshops(workshops);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = workshops.filter((workshop) => {
      const titleMatch = workshop.title?.toLowerCase().includes(term);
      return titleMatch;
    });

    setFilteredWorkshops(filtered);
  }, [searchTerm, workshops]);

  // Handle booking button click - open booking modal
  const handleBookWorkshop = (workshop) => {
    setBookingWorkshop(workshop);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white">
      {/* Navigation Bar with Profile/Logout */}
      <VisitorNavBar showBackButton backTo="/discover" />
      
      {/* Hero Section */}
      <div className="relative pt-24 pb-12 px-4">
        {/* Header */}
        <div className="bg-gray-950/50 backdrop-blur-md border-b border-white/10 sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/discover")}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <h1 className="text-2xl font-bold">Workshops</h1>
              </div>

              <div className="flex items-center">
                <button
                  onClick={() => {
                    try {
                      localStorage.setItem(
                        "mudo:switchToStudioIntentFrom",
                        window.location.pathname + window.location.search
                      );
                    } catch {}
                    localStorage.setItem("mudo:userRole", "studio");
                    navigate("/dashboard");
                  }}
                  className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm transition-colors"
                >
                  Switch to Studio
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 relative min-w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="text"
                  placeholder="Search by title, studio, or city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="inline-block w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-lg text-white/70">Loading workshops...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-20">
              <div className="mb-4 text-red-500">
                <Calendar className="w-16 h-16 mx-auto" />
              </div>
              <p className="text-lg text-white mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 rounded-lg bg-amber-600 hover:bg-amber-700 transition-colors font-semibold"
              >
                Retry
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && filteredWorkshops.length === 0 && (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-white/30" />
              <p className="text-lg text-white/70 mb-2">
                {searchTerm
                  ? "No workshops match your search"
                  : "No workshops found"}
              </p>
              <p className="text-sm text-white/50">
                {searchTerm
                  ? "Try a different search term"
                  : "Check back soon for upcoming workshops!"}
              </p>
            </div>
          )}

          {/* Workshops Grid */}
          {!loading && !error && filteredWorkshops.length > 0 && (
            <>
              <p className="text-sm text-white/70 mb-6">
                Found {filteredWorkshops.length}{" "}
                {filteredWorkshops.length === 1 ? "workshop" : "workshops"}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredWorkshops.map((workshop) => (
                  <WorkshopCard
                    key={workshop.id}
                    workshop={workshop}
                    onBook={handleBookWorkshop}
                    onImageClick={(w) =>
                      w.hero_image_url && setFullscreenWorkshop(w)
                    }
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Fullscreen Image View */}
        {fullscreenWorkshop && (
          <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col">
            <div className="flex items-center justify-between px-4 py-4">
              <button
                onClick={() => setFullscreenWorkshop(null)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-sm transition-colors text-white"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center px-4 pb-6">
              <img
                src={fullscreenWorkshop.hero_image_url}
                alt={fullscreenWorkshop.title}
                className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl border border-white/15"
              />
            </div>
          </div>
        )}

        {/* Workshop Booking Modal */}
        {bookingWorkshop && (
          <WorkshopBookingModalVisitor
            workshop={bookingWorkshop}
            onClose={() => setBookingWorkshop(null)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * WorkshopCard Component
 */
function WorkshopCard({ workshop, onBook, onImageClick }) {
  const seatsAvailable = workshop.capacity - workshop.seats_taken;
  const isFull = seatsAvailable <= 0;

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300 group">
      {/* Workshop Image (5:4 ratio, clickable) */}
      <button
        type="button"
        className="block w-full focus:outline-none"
        onClick={() => onImageClick && onImageClick(workshop)}
        disabled={!workshop.hero_image_url}
      >
        <div className="relative w-full pb-[80%] bg-gradient-to-br from-amber-500/30 to-orange-500/30 overflow-hidden">
          {workshop.hero_image_url ? (
            <img
              src={workshop.hero_image_url}
              alt={workshop.title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="absolute inset-0 w-full h-full flex items-center justify-center">
              <Calendar className="w-16 h-16 text-white/30" />
            </div>
          )}

          {/* Full Badge */}
          {isFull && (
            <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-red-600 text-white text-xs font-semibold">
              Full
            </div>
          )}

          {/* Skill Level */}
          {workshop.skill_level && (
            <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-semibold">
              {workshop.skill_level}
            </div>
          )}
        </div>
      </button>

      {/* Content */}
      <div className="p-6">
        {/* Title */}
        <h3 className="text-xl font-bold mb-2 line-clamp-2 group-hover:text-amber-500 transition-colors">
          {workshop.title}
        </h3>

        {/* Studio Name removed — rendering only DB row fields */}

        {/* Details */}
        <div className="space-y-2 mb-4 text-sm text-white/70">
          {/* Date & Time */}
          <div className="flex items-start gap-2">
            <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span className="line-clamp-2">
              {formatDateRange(workshop.start_at, workshop.end_at)}
            </span>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>
              {getDuration(workshop.start_at, workshop.end_at)} hours
            </span>
          </div>

          {/* Location removed — no studio join in DB rows */}

          {/* Capacity */}
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 flex-shrink-0" />
            <span>
              {isFull ? (
                "Fully booked"
              ) : (
                <>
                  {seatsAvailable}{" "}
                  {seatsAvailable === 1 ? "spot" : "spots"} left
                </>
              )}
            </span>
          </div>
        </div>

        {/* Price & Book Button */}
        <div className="flex items-center justify-between pt-4 border-t border-white/10">
          <div className="text-2xl font-bold text-amber-500">
            {formatPrice(workshop.price_cents, workshop.currency)}
          </div>

          <button
            onClick={() => onBook(workshop)}
            disabled={isFull}
            className={`px-6 py-2 rounded-lg font-semibold transition-all ${
              isFull
                ? "bg-white/5 text-white/30 cursor-not-allowed"
                : "bg-amber-600 hover:bg-amber-700 text-white hover:scale-105"
            }`}
          >
            {isFull ? "Full" : "View & Book"}
          </button>
        </div>
      </div>
    </div>
  );
}
